import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { GetStudioMasterListService } from './api-services/get-studio-master-list.service';
import { StudioMasterComboBoxModel } from './studio-master-combo-box.model';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 撮影場所コンボボックスを制御するクラス
 *
 * @export CodeComboBoxComponent
 * @class CodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-studio-master-combo-box',
  templateUrl: 'studio-master-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: StudioMasterComboBoxComponent,
      multi: true
    },
    {
      provide: GetStudioMasterListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetStudioMasterListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }
  ],
})

export class StudioMasterComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  public selectedStudioId: string = <string>'';                                 // 撮影場所ID
  public selectedStudioName: string = <string>'';                               // 撮影場所名
  public selectedIndex: number = <number>-1;                                    // 選択した番号

  public model: StudioMasterComboBoxModel = new StudioMasterComboBoxModel();

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() initAutoLoad: boolean = <boolean>true;                               // オードロードフラグ
  @Input() public tabindex: number = null;
  @Input() forcedEnabledMode: string = null;

  // レイアウト系の変数
  @Input() left: number = <number>0;
  @Input() top: number = <number>0;
  @Input() width: number | string; // どちらでも受けてTemplateに判断させる
  @Input() height: number = <number>20;
  @Input() disabled: boolean = <boolean>false;
  @Input() required: boolean = <boolean>false;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid
  @Input() afterFocus: any = null;                                              // ロード完了後フォーカスを移動させるid
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ

  /**
   * Creates an instance of codeComboBox.
   * @memberof StudioMasterComboBox
   * @param {getStudioMasterListService} GetStudioMasterListService 撮影場所リストを取得する
   */
  constructor(
    public elementRef: ElementRef,
    private getStudioMasterListService: GetStudioMasterListService,
  ) {
  }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedStudioId) {
      this.selectedStudioId = text;
      this.setStudioId(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof StudioMasterComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof StudioMasterComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getStudioMasterListService.execute(this.model);
    }
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof StudioMasterComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof StudioMasterComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * クリア
   *
   * @memberof StudioMasterComboBoxComponent
   */
  public clear(): void {
    // コンボボックス情報初期化
    this.selectedStudioId = null;                                               // 撮影場所ID
    this.selectedStudioName = null;                                             // 撮影場所名
    this.selectedIndex = null;                                                  // 選択した番号
    this.model = new StudioMasterComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * コンボボックスの選択撮影場所名を切り替える
   *
   * @param {string} StudioId 撮影場所ID
   * @memberof StudioMasterComboBoxComponent
   */
  public setStudioId(StudioId: string): void {
    this.model.setStudioId = StudioId;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedStudioId();
    }
  }

  /**
   * コンボボックスの選択撮影場所名を切り替える
   *
   * @memberof StudioMasterComboBoxComponent
   */
  public updateSelectedStudioId(): void {
    const studioMasterList: Array<any> = this.model.studioMasterList;
    for (let i = Number(0); i < studioMasterList.length; i++) {
      if (studioMasterList[i].StudioId === this.model.setStudioId) {
        this.selectedIndex = i;
      }
    }

    if (this.afterFocus != null) {
      if (this.afterFocus instanceof HTMLElement) {
        this.afterFocus.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.afterFocus.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }
    }

    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * バインドフィールド初期化
   *
   * @private
   * @param {*} e
   * @returns {void}
   * @memberof StudioMasterComboBoxComponent
   */
  public updateBindedField(e: any): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedStudioId = null;                                             // 撮影場所ID
      this.selectedStudioName = null;                                           // 撮影場所名

      return;
    }

    this.selectedStudioId = this.model.studioMasterList[this.selectedIndex].StudioId;
    this.selectedStudioName = this.model.studioMasterList[this.selectedIndex].StudioName;

    // 同じ意味合いのafterFocusがあればinitFocusItemに入れる
    if (this.afterFocus != null) {
      this.initFocusItem = this.afterFocus;
    }

    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }

}
